'use client'

import { Avatar } from '@repo/ui/Avatar'
import { CircledCheckIcon } from '@repo/ui/CircledCheckIcon'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { CustomToolbarTableModule } from '@repo/clients-matters-tables/CustomToolbarTableModule'
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridRenderHeaderFilterProps,
  GridSortModel,
  GridColumnMenu,
  GridColumnMenuProps,
  gridColumnDefinitionsSelector,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import {
  DateDataGridFilter,
  FeeEarnerDataGridFilter,
  KeyDateTypeDataGridFilter,
  SingleSelectDataGridFilter,
  StatusDataGridFilter,
  TextFieldDataGridFilter
} from '@repo/clients-matters-tables/DataGridTableCustomFilters'
import { extractClientMatterNumbers } from '@repo/utils/clientMatterNumbers'
import { formatDate, formatTime, dateIsToday, dateIsInThePast } from '@repo/utils/time'
import { KeyDateLocationSelect } from '@repo/ui/KeyDateLocationSelect'
import { KeyDateTypeSelect } from '@repo/ui/KeyDateTypeSelect'
import { LinkWithClientMatterNo } from '@repo/app-assets/LinkWithClientMatterNo'
import { Pill } from '@repo/ui/Pill'
import { TableClearFilters } from '@repo/ui/TableClearFilters'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClientStore } from '@repo/store/useClientStore'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMatterWorkflowStore } from '@repo/store/useMatterWorkflowStore'
import { useUserPreferencesStore } from '@repo/store/useUserPreferencesStore'
import { useWindowSize } from '@uidotdev/usehooks'
import { useUrlStore } from '@repo/store/useUrlStore'
import { v4 as uuidv4 } from 'uuid'
import defaultKeyDatesTableConfig from '@repo/store/defaultKeyDatesTableConfig'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

const parseMaybeJson = (val: unknown) => {
  if (!val) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch {
      return {}
    }
  }
  if (typeof val === 'object') return val as Record<string, any>
  return {}
}

export const KeyDatesTableModule = ({ isMatterSpecific = false }) => {
  const apiRef = useGridApiRef()

  const { setCurrentClient } = useClientStore()
  const { currentClientMatterNo } = useMattersStore()
  const {
    fetchKeyDatesTableData,
    keyDatesTableData,
    toggleKeyDatesDrawer,
    triggerKeyDatesTableRefresh,
    keyDateLocations
  } = useKeyDateStore()
  const { Matters: keyDatesPreferences, updateUserPreference } = useUserPreferencesStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const {
    keyDatesTableFilterCount,
    keyDatesTableFilters,
    matterSpecificKeyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilters,
    resetKeyDatesTableFilters,
    resetMatterSpecificKeyDatesTableFilters,
    setKeyDatesTableFilters,
    setMatterSpecificKeyDatesTableFilters,
    setKeyDatesTableFiltersFromPreferences,
    setMatterSpecificKeyDatesTableFiltersFromPreferences
  } = useDataGridFilterStore()
  const { height: windowHeight } = useWindowSize()
  const { navigateWithClientMatterNo } = useUrlStore()
  const { getWorkflowLink } = useMatterWorkflowStore()

  const [filtersReady, setFiltersReady] = useState(false)

  const activeFilters = isMatterSpecific ? matterSpecificKeyDatesTableFilters : keyDatesTableFilters
  const activeFilterCount = isMatterSpecific ? matterSpecificKeyDatesTableFilterCount : keyDatesTableFilterCount

  const setActiveFilters = useCallback(
    (patch: Partial<(typeof activeFilters)['filters']> | Record<string, any>) => {
      setKeyDatesTableFilters({ ...patch, isMatterSpecific })
    },
    [isMatterSpecific, setKeyDatesTableFilters]
  )

  const resetActiveFilters = useCallback(() => {
    if (isMatterSpecific) {
      resetMatterSpecificKeyDatesTableFilters()
    } else {
      resetKeyDatesTableFilters()
    }
  }, [isMatterSpecific, resetKeyDatesTableFilters, resetMatterSpecificKeyDatesTableFilters])

  const setActiveFiltersFromPreferences = useCallback(
    (args: { filters: any }) => {
      if (isMatterSpecific) {
        setMatterSpecificKeyDatesTableFiltersFromPreferences(args)
      } else {
        setKeyDatesTableFiltersFromPreferences(args)
      }
    },
    [isMatterSpecific, setKeyDatesTableFiltersFromPreferences, setMatterSpecificKeyDatesTableFiltersFromPreferences]
  )

  const activePref = useMemo(() => {
    return isMatterSpecific
      ? keyDatesPreferences?.KeyDate_GetAllExtendedBySearchCriteria_Matter
      : keyDatesPreferences?.KeyDate_GetAllExtendedBySearchCriteria_All
  }, [isMatterSpecific, keyDatesPreferences])

  const resolvedConfig = useMemo(() => {
    const rv = activePref?.resolvedValue ?? activePref?.userValue ?? activePref?.defaultValue ?? {}
    return parseMaybeJson(rv)
  }, [activePref])

  const activePrefId = activePref?.userConfigurationTypeId ?? null

  const OPERATOR_KEYS = new Set([
    'numberComparisonOperator',
    'dateComparisonOperator',
    'comparisonOperator',
    'operator'
  ])

  const isEmptyPrimitive = (x: unknown) => x === null || x === undefined || (typeof x === 'string' && x.trim() === '')

  const isFilterValueActive = (v: any): boolean => {
    if (isEmptyPrimitive(v)) return false
    if (Array.isArray(v)) return v.some((item) => !isEmptyPrimitive(item))
    switch (typeof v) {
      case 'boolean':
        return true
      case 'number':
        return true
      case 'string':
        return v.trim() !== ''
      case 'object': {
        if ('value' in v) return !isEmptyPrimitive((v as any).value)
        if ('date' in v) return !isEmptyPrimitive((v as any).date)
        return Object.entries(v as Record<string, any>).some(
          ([k, val]) => !OPERATOR_KEYS.has(k) && isFilterValueActive(val)
        )
      }
      default:
        return false
    }
  }

  const areFiltersEffectivelyEmpty = (f: Record<string, any> | null | undefined) => {
    if (!f) return true
    return !Object.values(f).some((v) => isFilterValueActive(v))
  }

  const emptyOutFilters = (f: Record<string, any> | null | undefined) => {
    if (!f) return {}
    const out: Record<string, any> = {}
    for (const [k, v] of Object.entries(f)) {
      if (v && typeof v === 'object') {
        if ('date' in v || 'dateComparisonOperator' in v) {
          out[k] = { date: null, dateComparisonOperator: 0 }
        } else if ('value' in v || 'numberComparisonOperator' in v) {
          out[k] = { value: null, numberComparisonOperator: 0 }
        } else {
          out[k] = null
        }
      } else {
        out[k] = null
      }
    }
    return out
  }

  const prefsHydratedRef = useRef(false)
  const firstSyncDoneRef = useRef(false)
  const userClearedRef = useRef(false)

  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({})
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({ left: [], right: [] })
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 216 : 0)

  useEffect(() => {
    const prefBag = resolvedConfig?.keyDatesTableFilters ?? null
    const storeEmpty = areFiltersEffectivelyEmpty(activeFilters?.filters)
    const prefsEmpty = areFiltersEffectivelyEmpty(prefBag)

    if (prefsHydratedRef.current) {
      setFiltersReady(true)
      return
    }

    if (!storeEmpty) {
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    if (!prefsEmpty) {
      setActiveFiltersFromPreferences({ filters: prefBag ?? null })
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    setFiltersReady(true)
  }, [resolvedConfig?.keyDatesTableFilters, activeFilters?.filters, isMatterSpecific, setActiveFiltersFromPreferences])

  useEffect(() => {
    const fallback = defaultKeyDatesTableConfig || {}
    setColumnOrder(resolvedConfig.columnOrder ?? fallback.columnOrder ?? [])
    setColumnVisibilityModel(resolvedConfig.columnVisibilityModel ?? fallback.columnVisibilityModel ?? {})
    setColumnWidths(resolvedConfig.columnWidths ?? fallback.columnWidths ?? {})
    setPinnedColumns({
      left: resolvedConfig.columnsPinnedLeft ?? fallback.columnsPinnedLeft ?? [],
      right: resolvedConfig.columnsPinnedRight ?? fallback.columnsPinnedRight ?? []
    })
    setSortModel(resolvedConfig.columnSort ?? fallback.columnSort ?? [{ field: 'date', sort: 'desc' }])
  }, [resolvedConfig])

  const updateTableConfigPreference = useCallback(
    (fieldsToChange: Record<string, any>) => {
      if (!activePrefId) return
      const next = {
        ...resolvedConfig,
        columnOrder: columnOrder,
        columnVisibilityModel: columnVisibilityModel,
        columnWidths: columnWidths,
        columnsPinnedLeft: pinnedColumns.left,
        columnsPinnedRight: pinnedColumns.right,
        columnSort: sortModel,
        ...fieldsToChange
      }

      updateUserPreference({
        userConfigurationTypeId: activePrefId,
        value: next,
        convertJsonToString: true,
        categoryName: 'Matters'
      })
    },
    [
      activePrefId,
      resolvedConfig,
      updateUserPreference,
      sortModel,
      columnOrder,
      columnVisibilityModel,
      columnWidths,
      pinnedColumns
    ]
  )

  const onRowClick = (params: any) => {
    toggleKeyDatesDrawer({
      keyDatesDrawerIsOpen: true,
      keyDatesDrawerMode: 'view',
      keyDatesDrawerItemId: params.row.keyDateId
    })
  }

  const onPinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumnFields) => {
      setPinnedColumns(updatedPinnedColumns)
      updateTableConfigPreference({
        columnsPinnedLeft: updatedPinnedColumns.left,
        columnsPinnedRight: updatedPinnedColumns.right
      })
    },
    [updateTableConfigPreference]
  )

  const onSortModelChange = (model: GridSortModel) => {
    setSortModel(model)
    updateTableConfigPreference({ columnSort: model })
  }

  const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model)
    updateTableConfigPreference({ columnVisibilityModel: model })
  }

  const onColumnOrderChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const nextOrder: any = columnDefinitions.map((column: any) => column.field)
    setColumnOrder(nextOrder)
    updateTableConfigPreference({ columnOrder: nextOrder })
  }

  const onColumnWidthChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const nextWidths: any = {}
    columnDefinitions.forEach((column: any) => {
      nextWidths[column.field] = column.width
    })
    setColumnWidths(nextWidths)
    updateTableConfigPreference({ columnWidths: nextWidths })
  }

  const useDeepCompareMemoize = (value: any) => {
    const ref = useRef(value)
    const signal = JSON.stringify(value)
    const prevSignal = JSON.stringify(ref.current)
    if (signal !== prevSignal) {
      ref.current = value
    }
    return ref.current
  }

  const deepFilters = useDeepCompareMemoize(activeFilters)

  useEffect(() => {
    if (!filtersReady) return

    const getTableData = async () => {
      setLoading(true)

      const computedOrderBy = sortModel
        .map((sortItem) => {
          const fieldMap: Record<string, string> = {
            clientMatterNo: 'clientNo',
            date: 'date'
          }
          const columnName = fieldMap[sortItem.field] ?? sortItem.field
          return { columnName, direction: sortItem.sort === 'asc' ? 0 : 1 }
        })
        .filter(Boolean)

      const orderByColumns = computedOrderBy.some((item: any) => item.columnName === null) ? null : computedOrderBy

      const { clientNo, matterNo } =
        isMatterSpecific && currentClientMatterNo
          ? extractClientMatterNumbers({ clientMatterNo: currentClientMatterNo })
          : { clientNo: null, matterNo: null }

      const payload = isMatterSpecific
        ? {
            ...deepFilters,
            clientMatterNo: currentClientMatterNo,
            filters: {
              ...deepFilters.filters,
              clientNo,
              matterNo,
              orderByColumns,
              startAtIndex: deepFilters.filters?.startAtIndex ?? 0,
              pageSize: deepFilters.filters?.pageSize ?? 25
            }
          }
        : {
            ...deepFilters,
            filters: {
              ...deepFilters.filters,
              orderByColumns,
              startAtIndex: deepFilters.filters?.startAtIndex ?? 0,
              pageSize: deepFilters.filters?.pageSize ?? 25
            }
          }

      const deepFiltersFiltersString = JSON.stringify(deepFilters.filters)
      const currentTableConfigFiltersString = JSON.stringify(resolvedConfig?.keyDatesTableFilters ?? {})
      const filtersHaveChanged = deepFiltersFiltersString !== currentTableConfigFiltersString

      const deepEmpty = areFiltersEffectivelyEmpty(deepFilters.filters)
      const prefsEmpty = areFiltersEffectivelyEmpty(resolvedConfig?.keyDatesTableFilters)
      const safeToSync = firstSyncDoneRef.current ? true : false

      setKeyDatesTableFilters({
        isMatterSpecific,
        clientMatterNo: payload.clientMatterNo,
        filters: payload.filters // <-- merge clientNo/matterNo/orderByColumns/pageSize/startAtIndex
      })

      if (safeToSync && filtersHaveChanged) {
        if (!(deepEmpty && !prefsEmpty)) {
          updateTableConfigPreference({ keyDatesTableFilters: deepFilters.filters })
        }
      }

      await fetchKeyDatesTableData({ keyDatesTableFilters: payload })

      setLoading(false)

      if (!firstSyncDoneRef.current) firstSyncDoneRef.current = true
    }

    getTableData()
  }, [
    filtersReady,
    deepFilters.filters,
    isMatterSpecific,
    sortModel,
    triggerKeyDatesTableRefresh,
    currentClientMatterNo,
    resolvedConfig?.keyDatesTableFilters,
    fetchKeyDatesTableData,
    updateTableConfigPreference
  ])

  useEffect(() => {
    isMatterSpecific
      ? setKeyDatesTableFilters({ totalRowCount: keyDatesTableData.totalRowCount, isMatterSpecific })
      : setKeyDatesTableFilters({ totalRowCount: keyDatesTableData.totalRowCount })
  }, [keyDatesTableData.totalRowCount, isMatterSpecific, setKeyDatesTableFilters])

  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 216 : 146
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  const handleClearAll = useCallback(() => {
    userClearedRef.current = true
    resetActiveFilters()
    const currentPrefFilters = resolvedConfig?.keyDatesTableFilters
    const emptied = emptyOutFilters(currentPrefFilters ?? activeFilters?.filters ?? {})
    updateTableConfigPreference({ keyDatesTableFilters: emptied })
  }, [resetActiveFilters, resolvedConfig?.keyDatesTableFilters, activeFilters?.filters, updateTableConfigPreference])

  const columns: any = [
    {
      field: 'clientMatterNo',
      // @ts-ignore
      id: 'clientMatterNo',
      headerName: 'Matter',
      width: columnWidths?.['clientMatterNo'] || 150,
      type: 'string',
      hideable: false,
      renderCell: (params: any) => (
        <ClientMatterDetailsPill
          matterNo={params.row.matterNo}
          clientNo={params.row.clientNo}
          sx={{ top: '3px' }}
          isUserRestricted={params.row.isUserRestricted}
        />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter
          key={`clientNo-${isMatterSpecific ? 'matter' : 'global'}`}
          filterType="clientNo"
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
          {...params}
        />
      )
    },
    {
      field: 'date',
      id: 'date',
      headerName: 'Date',
      width: columnWidths?.['date'] || 190,
      type: 'custom',
      renderCell: (params: any) => {
        const isToday = params.value ? dateIsToday(params.value) : false
        const isInThePast = params.value ? dateIsInThePast(params.value) : false
        const highlightColor = isToday ? 'warningLight.main' : isInThePast ? 'error.main' : 'success.main'

        return params.value ? (
          <MUITooltip title={isToday ? 'Due today' : isInThePast ? 'Overdue' : 'Due soon'} arrow placement="top">
            <MUITypography
              variant="body2"
              sx={{
                mb: 0,
                padding: '8px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                color: isToday ? 'warningLight.main' : isInThePast ? 'error.main' : 'inherit',
                border: 'none',
                borderLeftStyle: 'solid',
                borderWidth: '4px',
                borderColor: highlightColor
              }}
            >
              {formatDate(params.value)}
            </MUITypography>
          </MUITooltip>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
      },
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <DateDataGridFilter
          key={`date-${isMatterSpecific ? 'matter' : 'global'}`}
          filterType="keyDate"
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
          {...params}
        />
      )
    },
    {
      field: 'clientNames',
      headerName: 'Client names',
      width: columnWidths?.['clientNames'] || 260,
      sortable: false,
      type: 'string',
      renderCell: (params: any) => {
        const clientNames = params.row.clientNames
        if (clientNames.length > 1) {
          return (
            <MUITooltip
              placement="bottom"
              arrow
              sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
              title={clientNames.map((client: any) => (
                <MUITypography
                  key={client.name}
                  variant="body2"
                  color="white"
                  sx={{
                    mb: 1,
                    p: 1,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textTransform: 'capitalize',
                    fontSize: '0.75rem',
                    whiteSpace: 'wrap',
                    lineHeight: '1.2rem'
                  }}
                >
                  {client.name}
                </MUITypography>
              ))}
            >
              <LinkWithClientMatterNo
                to={`/case-management/clients/client-workspace`}
                clientMatterNo={`${params.row.clientNo}-${params.row.mostRecentMatterNo || params.row.matterNo}`}
                stopPropagation
              >
                <MUIStack display="flex" direction="row" gap={1.5} sx={{ cursor: 'pointer' }}>
                  <MUITypography
                    variant="body2"
                    sx={{
                      mb: 0,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textTransform: 'capitalize',
                      fontSize: '0.875rem',
                      whiteSpace: 'wrap',
                      lineHeight: '1.2rem'
                    }}
                  >
                    {clientNames[0].name}
                  </MUITypography>
                  <MUITypography
                    variant="body2"
                    sx={{
                      mb: 0,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textTransform: 'capitalize',
                      fontSize: '0.875rem',
                      whiteSpace: 'wrap',
                      lineHeight: '1.2rem'
                    }}
                  >
                    +{clientNames.length - 1}
                  </MUITypography>
                </MUIStack>
              </LinkWithClientMatterNo>
            </MUITooltip>
          )
        } else {
          return (
            <MUIStack display="flex" direction="row" gap={1.5}>
              {clientNames.map((client: any) => (
                <LinkWithClientMatterNo
                  to={`/case-management/clients/client-workspace`}
                  clientMatterNo={`${params.row.clientNo}-${params.row.mostRecentMatterNo || params.row.matterNo}`}
                  key={params.row.clientNo}
                  stopPropagation
                >
                  <MUITypography
                    key={client.name}
                    variant="body2"
                    sx={{
                      mb: 0,
                      fontSize: '0.875rem',
                      cursor: client.name !== '' ? 'pointer' : 'auto',
                      textDecoration: client.name !== '' ? 'underline' : 'none',
                      textTransform: client.name !== '' ? 'capitalize' : 'none',
                      whiteSpace: 'wrap',
                      lineHeight: '1.2rem'
                    }}
                  >
                    {client.name !== '' ? client.name : '-'}
                  </MUITypography>
                </LinkWithClientMatterNo>
              ))}
            </MUIStack>
          )
        }
      },
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter
          key={`clientName-${isMatterSpecific ? 'matter' : 'global'}`}
          filterType="clientName"
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
          {...params}
        />
      )
    },
    {
      field: 'startTime',
      headerName: 'Start time',
      width: columnWidths?.['startTime'] || 160,
      type: 'number',
      filterable: false,
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem', cursor: 'pointer' }}>
            {formatTime(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem', cursor: 'pointer' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'durationMinutes',
      headerName: 'Duration',
      width: columnWidths?.['durationMinutes'] || 160,
      type: 'number',
      filterable: false,
      renderCell: (params: any) => {
        const duration = params.row.durationMinutes
        return (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem', cursor: 'pointer' }}>
            {duration ? `${duration} mins` : '-'}
          </MUITypography>
        )
      }
    },
    {
      field: 'dateMet',
      headerName: 'Date met',
      width: columnWidths?.['dateMet'] || 200,
      type: 'custom',
      renderCell: (params: any) =>
        params.value === true ? (
          <Pill
            label={'Date met'}
            backgroundColor={'secondary.main'}
            color={'white'}
            icon={<CircledCheckIcon sx={{ width: '14px', height: '14px', color: 'white', ml: 1, mr: 0 }} />}
          />
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <SingleSelectDataGridFilter
          key={`dateMet-${isMatterSpecific ? 'matter' : 'global'}`}
          filterType="dateMet"
          label="Select"
          valueMapping={{ true: 'Date Met', false: 'Date Not Met' }}
          options={[true, false] as any}
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
          {...params}
        />
      )
    },
    {
      field: 'keyDateTypeDescription',
      // @ts-ignore
      id: 'keyDateTypeDescription',
      headerName: 'Key date type',
      width: columnWidths?.['keyDateTypeDescription'] || 280,
      type: 'string',
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.row.keyDateTypeDescription ? (
            <Pill label={params.row.keyDateTypeDescription} backgroundColor="background.mid" />
          ) : (
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              -
            </MUITypography>
          )}
        </MUIStack>
      ),
      renderHeaderFilter: (_params: GridRenderHeaderFilterProps) => {
        const selectedId = activeFilters?.filters?.keyDateTypeIds?.[0] ?? null

        const fromRows =
          (keyDatesTableData?.rows || []).find(
            (r: any) =>
              r?.keyDateTypeId === selectedId ||
              (Array.isArray(r?.keyDateTypeIds) && r.keyDateTypeIds.includes(selectedId))
          ) ?? null

        const label = fromRows?.keyDateTypeDescription ?? (selectedId != null ? String(selectedId) : '')

        const selectedValue = selectedId == null ? null : { keyDateTypeId: selectedId, description: label }

        return (
          <KeyDateTypeSelect
            label="Select"
            size="small"
            name="keyDateTypeIds"
            value={selectedValue}
            onChange={(val: any) => {
              const id =
                typeof val === 'number'
                  ? val
                  : (val?.keyDateTypeId ?? val?.value ?? (typeof val === 'string' ? val : null))

              setKeyDatesTableFilters({
                filterType: 'keyDateTypeIds',
                value: id ? [id] : null,
                isMatterSpecific
              })
              setKeyDatesTableFilters({ startAtIndex: 0, isMatterSpecific }) // reset pagination
            }}
            keyDateTable={true}
          />
        )
      }
    },
    {
      field: 'keyDateLocationDescription',
      // @ts-ignore
      id: 'keyDateLocationDescription',
      headerName: 'Key date location',
      width: columnWidths?.['keyDateLocationDescription'] || 280,
      type: 'string',
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.row.keyDateLocationDescription ? (
            <MUITypography variant="body1" sx={{ mb: 0, cursor: 'pointer' }}>
              {params.row.keyDateLocationDescription}
            </MUITypography>
          ) : (
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              -
            </MUITypography>
          )}
        </MUIStack>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        const currentDesc = (activeFilters?.filters?.keyDateLocationId ?? '') as string

        // try to map it to the option object so the select shows the current value
        const selectedOption =
          (keyDateLocations || []).find((opt: any) => opt?.keyDateLocationId === currentDesc) ??
          (currentDesc
            ? { keyDateLocationId: null, description: currentDesc } // fallback if it isn't in the cached list yet
            : null)

        return (
          <KeyDateLocationSelect
            label="Select"
            size="small"
            value={selectedOption}
            keyDateTable={true}
            onChange={(val: any) => {
              // persist description to the existing filter key
              setKeyDatesTableFilters({
                filterType: 'keyDateLocationId',
                value: val?.keyDateLocationId ?? null,
                isMatterSpecific
              })
              // reset pagination so results start from page 1 after changing this filter
              setKeyDatesTableFilters({ startAtIndex: 0, isMatterSpecific })
            }}
            sx={{
              width: '100%',
              [`& .MuiStack-root`]: {
                // minHeight: 0,
                // height: '33px',
                // mt: '3px'
                backgroundColor: 'green'
              }
            }}
          />
        )
      }
    },
    {
      field: 'mustAttend',
      headerName: 'Attendance',
      width: columnWidths?.['mustAttend'] || 200,
      type: 'custom',
      renderCell: (params: any) =>
        params.value === true || params.value === false ? (
          <Pill
            label={params.value === true ? 'Must attend' : 'Optional attendance'}
            backgroundColor={params.value === true ? 'info.dark' : 'background.mid'}
            color={params.value === true ? 'white' : 'black'}
          />
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <SingleSelectDataGridFilter
          key={`mustAttend-${isMatterSpecific ? 'matter' : 'global'}`}
          filterType="mustAttend"
          label="Select"
          valueMapping={{ true: 'Must attend', false: 'Optional attendance' }}
          options={[true, false] as any}
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
          {...params}
        />
      )
    },
    {
      field: 'notes',
      // @ts-ignore
      id: 'notes',
      headerName: 'Key date description',
      width: columnWidths?.['notes'] || 280,
      type: 'string',
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.row.notes ? (
            <MUITypography variant="body1" sx={{ mb: 0, cursor: 'pointer' }}>
              {params.row.notes}
            </MUITypography>
          ) : (
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              -
            </MUITypography>
          )}
        </MUIStack>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter
          key={`notes-${isMatterSpecific ? 'matter' : 'global'}`}
          filterType="notes"
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
          {...params}
        />
      )
    },
    {
      field: 'criminalNotes',
      // @ts-ignore
      id: 'criminalNotes',
      headerName: 'Additional details',
      width: columnWidths?.['criminalNotes'] || 280,
      type: 'string',
      sortable: false,
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.row.criminalNotes ? (
            <MUITypography>{params.row.criminalNotes}</MUITypography>
          ) : (
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              -
            </MUITypography>
          )}
        </MUIStack>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter
          key={`criminalNotes-${isMatterSpecific ? 'matter' : 'global'}`}
          filterType="criminalNotes"
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
          {...params}
        />
      )
    },
    {
      field: 'holidayStatus',
      id: 'holidayStatus',
      headerName: 'Holiday status',
      width: columnWidths?.['holidayStatus'] || 280,
      type: 'singleSelect',
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.row.isWeekend === true ? (
            <Pill
              label="Weekend"
              color={'white'}
              backgroundColor={'warning.light'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : params.row.isHoliday === true ? (
            <Pill
              label="Bank holiday"
              color={'white'}
              backgroundColor={'warning.light'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : (
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              -
            </MUITypography>
          )}
        </MUIStack>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <StatusDataGridFilter
          key={`holidayStatus-${isMatterSpecific ? 'matter' : 'global'}`}
          {...params}
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
        />
      )
    },
    {
      field: 'feeEarnerDescription',
      headerName: 'Fee earner',
      width: columnWidths?.['feeEarnerDescription'] || 280,
      type: 'custom',
      renderCell: (params: any) => {
        return (
          <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
            <Avatar
              feeEarnerId={params.row.feeEarnerId}
              name={params.value}
              sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
            />
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              {params.value}
            </MUITypography>
          </MUIStack>
        )
      },
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <FeeEarnerDataGridFilter
          filterType="feeEarnerIds"
          {...params}
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
        />
      )
    },
    {
      field: 'feeEarnerLegalAidPin',
      // @ts-ignore
      id: 'feeEarnerLegalAidPin',
      headerName: 'Fee earner Legal Aid PIN',
      width: columnWidths?.['feeEarnerLegalAidPin'] || 280,
      type: 'string',
      renderCell: (params: any) => (
        <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
          {params.value || '-'}
        </MUITypography>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter
          key={`feeEarnerLegalAidPin-${isMatterSpecific ? 'matter' : 'global'}`}
          filterType="feeEarnerLegalAidPin"
          placeholder="Exact match"
          isKeyDatesTable
          isMatterSpecific={isMatterSpecific}
          {...params}
        />
      )
    },
    {
      field: 'createdDate',
      headerName: 'Created date',
      width: columnWidths?.['createdDate'] || 190,
      type: 'custom',
      filterable: false,
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'createdBy',
      headerName: 'Created by',
      width: columnWidths?.['dateOpened'] || 160,
      type: 'string',
      filterable: false,
      renderCell: (params: any) => {
        return (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {params.value || params.row.userId || '-'}
          </MUITypography>
        )
      }
    },
    {
      field: 'updatedDate',
      headerName: 'Updated date',
      width: columnWidths?.['updatedDate'] || 190,
      type: 'custom',
      filterable: false,
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'updatedBy',
      headerName: 'Updated by',
      width: columnWidths?.['updatedBy'] || 160,
      type: 'string',
      filterable: false,
      renderCell: (params: any) => (
        <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
          {params.value || '-'}
        </MUITypography>
      )
    },
    {
      field: 'workflowTask',
      headerName: 'Workflow task',
      width: columnWidths?.['workflowTask'] || 160,
      type: 'custom',
      filterable: false,
      sortable: false,
      renderCell: (params: any) => {
        const onWorkflowTaskClick = (e: React.SyntheticEvent) => {
          if (
            params.row.clientNo &&
            params.row.matterNo &&
            params.row.actionLinkedTaskId &&
            params.row.taskPositionIndex &&
            params.row.workflowId &&
            params.row.uniqueWorkflowCount &&
            params.row.workflowTaskIsTicked === false
          ) {
            e.stopPropagation()

            const { clientNo, matterNo, actionLinkedTaskId, taskPositionIndex, workflowId, uniqueWorkflowCount } =
              params.row

            const route = getWorkflowLink({
              clientNo,
              matterNo,
              actionLinkedTaskId,
              taskPositionIndex,
              workflowId,
              uniqueWorkflowCount
            })

            navigateWithClientMatterNo({
              route,
              clientMatterNo: `${clientNo}-${matterNo}`,
              appendClientMatterNo: true
            })
          }
        }

        if (
          params.row.clientNo &&
          params.row.matterNo &&
          params.row.actionLinkedTaskId &&
          params.row.taskPositionIndex &&
          params.row.workflowId &&
          params.row.uniqueWorkflowCount &&
          params.row.workflowTaskIsTicked === false
        ) {
          return (
            <MUITypography
              onClick={onWorkflowTaskClick}
              onMouseDown={onWorkflowTaskClick}
              onTouchStart={onWorkflowTaskClick}
              onKeyDown={onWorkflowTaskClick}
              sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#5073C3' }}
            >
              Run task
            </MUITypography>
          )
        } else if (params.row.workflowTaskIsTicked === true) {
          return <MUITypography sx={{ cursor: 'default', pointerEvents: 'none' }}>Complete</MUITypography>
        } else {
          return <MUITypography sx={{ cursor: 'default', pointerEvents: 'none' }}>-</MUITypography>
        }
      }
    }
  ].sort((a, b) => {
    const targetIndexA = columnOrder?.indexOf(a.field) ?? 0
    const targetIndexB = columnOrder?.indexOf(b.field) ?? 1
    if (targetIndexA > targetIndexB) return 1
    return -1
  })

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => column.hideable !== false && column.field !== '__check__')
      .map((column) => column.field)
  }

  const paginationModel: GridPaginationModel = {
    page: Math.floor(
      (activeFilters.filters.startAtIndex === undefined || activeFilters.filters.startAtIndex === null
        ? 0
        : activeFilters.filters.startAtIndex) /
        (activeFilters.filters.pageSize === undefined || activeFilters.filters.pageSize === null
          ? 25
          : activeFilters.filters.pageSize)
    ),
    pageSize:
      activeFilters.filters.pageSize === undefined || activeFilters.filters.pageSize === null
        ? 25
        : activeFilters.filters.pageSize
  }

  return (
    <MUIPaper
      key={`keydates-paper-${isMatterSpecific ? 'matter' : 'global'}`}
      data-testid="KeyDatesTableModule"
      sx={{
        position: 'relative',
        fontSize: '0.875rem',
        height: `calc(${tableHeight}px - 20px)` || 'auto',
        width: '100%',
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)',
        // matter-specific key-dates no longer renders the workflow StatusBar (suppressed via the route's
        // hideStatusBar flag), so it uses a normal top margin instead of the previous negative-margin hack
        // that pulled the table up to cover the StatusBar. The global all-key-dates view keeps its original spacing.
        ...(isMatterSpecific
          ? { mt: 3 }
          : { mt: '9rem', [`@media (min-height: 991px)`]: { mt: '-3.5rem' } }),
        [`& .MuiDataGrid-root`]: {
          border: 'none',
          [`& div[field="clientNames"]`]: { cursor: 'pointer', [`& .MuiStack-root`]: { cursor: 'pointer' } }
        }
      }}
    >
      <TableClearFilters
        filterCount={activeFilterCount}
        onClear={handleClearAll}
        sx={{ position: 'absolute', top: '3px', right: '130px', zIndex: 2 }}
      />
      <DataGridPro
        key={`keydates-grid-${isMatterSpecific ? 'matter' : 'global'}`}
        apiRef={apiRef}
        columns={columns as GridColDef[]}
        columnVisibilityModel={columnVisibilityModel}
        disableVirtualization
        getRowId={(row) => `${row.clientNo}-${row.matterNo}-${row.date}-${uuidv4()}`}
        headerFilters
        loading={loading}
        onColumnOrderChange={onColumnOrderChange}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onColumnWidthChange={onColumnWidthChange}
        onPaginationModelChange={(newModel) => {
          setActiveFilters({
            pageSize: newModel.pageSize
          })
          setActiveFilters({
            startAtIndex: newModel.page * newModel.pageSize
          })
          setActiveFilters({
            totalRowCount: keyDatesTableData.totalRowCount
          })
        }}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onRowClick={onRowClick}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={[5, 10, 25, 50]}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        pinnedColumns={pinnedColumns}
        rowCount={keyDatesTableData.totalRowCount || 0}
        rows={keyDatesTableData.rows || []}
        slotProps={{
          columnsManagement: { getTogglableColumns },
          loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
        }}
        sortModel={sortModel}
        sx={{
          [`.MuiDataGrid-root`]: { border: 'none' },
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' },
          [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' },
          [`& .MuiDataGrid-cell`]: {
            cursor: 'pointer',
            alignContent: 'center',
            [`& .MuiTypography-root`]: { top: 'unset' }
          },
          [`& .MuiTablePagination-selectLabel`]: { display: 'block !important' },
          [`& .MuiTablePagination-input`]: {
            display: 'inline-flex !important',
            mb: 0,
            ml: 0,
            mt: '8px',
            fontSize: '0.875rem',
            [`& .MuiTablePagination-select`]: {
              padding: '0 !important',
              ml: '4px',
              mr: '20px',
              minWidth: 'none',
              [`&:focus`]: { backgroundColor: 'transparent' }
            },
            [`& .MuiSvgIcon-root`]: { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
          }
        }}
        slots={{
          columnMenu: (props: GridColumnMenuProps) => (
            <GridColumnMenu
              {...props}
              // @ts-ignore
              sx={{ '.MuiTypography-body1': { mb: 0 } }}
              slots={{ columnMenuFilterItem: null }} // Hides the "Filters" item
            />
          ),
          headerFilterMenu: null,
          toolbar: () => <CustomToolbarTableModule isKeyDatesTable isMatterSpecific={isMatterSpecific} />
        }}
      />
    </MUIPaper>
  )
}

KeyDatesTableModule.propTypes = {
  isMatterSpecific: PropTypes.bool
}
